import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { adminAPI } from '../../services/api';
import '../../styles/components.css';

const MONTH_NAMES = [
    'january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december'
];

const normalizeMonth = (value) => {
    const text = String(value ?? '').trim().toLowerCase();
    const numeric = Number(text);
    if (numeric >= 1 && numeric <= 12) return MONTH_NAMES[numeric - 1];
    return MONTH_NAMES.find(month => month === text || month.startsWith(text)) || text;
};

const normalizeYear = (value) => String(value ?? '').trim();

const getClaimTimestamp = (claim) => {
    const value = claim.created_at || claim.createdAt || claim.submittedAt;
    if (!value) return NaN;
    if (typeof value === 'object' && value._seconds) return value._seconds * 1000;
    if (typeof value.toDate === 'function') return value.toDate().getTime();
    return new Date(value).getTime();
};

const ManageClaims = () => {
    const navigate = useNavigate();
    const [claims, setClaims] = useState(null);
    const [filteredClaims, setFilteredClaims] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'pending', 'approved'
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkProcessing, setBulkProcessing] = useState(false);
    const [sortConfig, setSortConfig] = useState({ field: null, dir: null });
    const [filterMode, setFilterMode] = useState('month');
    const [selectedMonth, setSelectedMonth] = useState('');
    const [selectedYear, setSelectedYear] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const fetchClaims = async () => {
        if (filterMode === 'month' && (!selectedMonth || !selectedYear)) {
            setError('Please select both a month and year.');
            return;
        }
        if (filterMode === 'range' && (!fromDate || !toDate || fromDate > toDate)) {
            setError('Please select a valid From and To date.');
            return;
        }

        try {
            setLoading(true);
            setError('');
            const params = filterMode === 'month'
                ? { month: selectedMonth, year: selectedYear }
                : { fromDate, toDate };
            const response = await adminAPI.getClaims(params);
            const claimsData = response.data.claims || [];
            setClaims(claimsData);
            setSuccess('');
        } catch (err) {
            console.error('Error fetching claims:', err);
            setError(err.response?.data?.error || err.message || 'Failed to fetch claims');
        } finally {
            setLoading(false);
        }
    };

    const filterClaims = useCallback(() => {
        if (!claims) return;
        let filtered = [...claims];

        if (filterMode === 'month') {
            filtered = filtered.filter(claim =>
                normalizeMonth(claim.claim_month) === normalizeMonth(selectedMonth) &&
                normalizeYear(claim.claim_year) === normalizeYear(selectedYear)
            );
        } else if (filterMode === 'range') {
            const from = new Date(`${fromDate}T00:00:00`).getTime();
            const to = new Date(`${toDate}T23:59:59.999`).getTime();
            filtered = filtered.filter(claim => {
                const timestamp = getClaimTimestamp(claim);
                return timestamp >= from && timestamp <= to;
            });
        }

        if (filterStatus === 'pending') {
            filtered = filtered.filter(c => !c.isadminApproved || c.isadminApproved === 'pending');
        } else if (filterStatus === 'approved') {
            filtered = filtered.filter(c => c.isadminApproved === 'approved');
        } else if (filterStatus === 'rejected') {
            filtered = filtered.filter(c => c.isadminApproved === 'rejected');
        }

        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            filtered = filtered.filter(c =>
                c.user_email?.toLowerCase().includes(q) ||
                c.scholarName?.toLowerCase().includes(q) ||
                c.tceRollNo?.toLowerCase().includes(q) ||
                c.department?.toLowerCase().includes(q) ||
                c.claim_month?.toLowerCase().includes(q) ||
                c.claim_year?.toString().includes(q)
            );
        }

        setFilteredClaims(filtered);
    }, [claims, filterMode, selectedMonth, selectedYear, fromDate, toDate, filterStatus, searchQuery]);

    useEffect(() => {
        if (claims) filterClaims();
        setSelectedIds(new Set()); // reset selection when filter changes
        setSortConfig({ field: null, dir: null }); // reset sort when filter changes
    }, [claims, filterClaims]);

    const MONTHS = MONTH_NAMES.map(month => month[0].toUpperCase() + month.slice(1));
    const currentYear = new Date().getFullYear();
    const YEARS = Array.from({ length: currentYear - 2022 }, (_, i) => String(2023 + i));

    const toggleSelect = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        // Only allow selecting pending-admin claims for bulk approve
        const pendingIds = filteredClaims
            .filter(c => !c.isadminApproved || c.isadminApproved === 'pending')
            .map(c => c.id);

        if (selectedIds.size === pendingIds.length && pendingIds.every(id => selectedIds.has(id))) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(pendingIds));
        }
    };

    const handleBulkApprove = async () => {
        if (selectedIds.size === 0) return;
        if (!window.confirm(`Approve ${selectedIds.size} selected claim(s)?`)) return;

        setBulkProcessing(true);
        setError('');
        setSuccess('');
        try {
            const response = await adminAPI.bulkApproveClaims([...selectedIds]);
            setSuccess(response.data.message || `${selectedIds.size} claim(s) approved successfully!`);
            setSelectedIds(new Set());
            await fetchClaims();
        } catch (err) {
            setError(err.response?.data?.error || 'Bulk approval failed');
        } finally {
            setBulkProcessing(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    const getStatusBadge = (claim) => {
        const val = claim.isadminApproved || 'pending';

        const labelMap = {
            approved: 'Associate Dean (R&D) Approved',
            rejected: 'Rejected',
            pending: 'Associate Dean (R&D) Pending'
        };

        const styles = {
            approved: { background: '#dcfce7', color: '#166534' },
            rejected: { background: '#fee2e2', color: '#991b1b' },
            pending: { background: '#fef3c7', color: '#92400e' }
        };

        const s = styles[val] || styles.pending;

        return (
            <span
                style={{
                    padding: "0.25rem 0.75rem",
                    borderRadius: "1rem",
                    fontSize: "0.75rem",
                    fontWeight: "600",
                    background: s.background,
                    color: s.color,
                    display: "inline-block"
                }}
            >
                {labelMap[val] || 'Pending'}
            </span>
        );
    };

    const stats = {
        total: filteredClaims.length,
        pending: filteredClaims.filter(c => !c.isadminApproved || c.isadminApproved === 'pending').length,
        approved: filteredClaims.filter(c => c.isadminApproved === 'approved').length,
        rejected: filteredClaims.filter(c => c.isadminApproved === 'rejected').length,
        totalAmount: filteredClaims.reduce((sum, c) => sum + (Number(c.claim_amount) || 0), 0),
    };

    const pendingInView = filteredClaims.filter(c => !c.isadminApproved || c.isadminApproved === 'pending');
    const allPendingSelected = pendingInView.length > 0 && pendingInView.every(c => selectedIds.has(c.id));

    // ── Sorting ────────────────────────────────────────────────────────────────
    const cycleSort = (field) => {
        setSortConfig(prev => {
            if (prev.field !== field) return { field, dir: 'asc' };
            if (prev.dir === 'asc') return { field, dir: 'desc' };
            return { field: null, dir: null };
        });
    };
    const sortIcon = (field) => {
        if (sortConfig.field !== field) return '⇅';
        return sortConfig.dir === 'asc' ? '▲' : '▼';
    };
    const NUMERIC_SORT = new Set(['availedCL', 'balCL', 'llp', 'amount', 'slNo']);
    const sortedClaims = (() => {
        if (!sortConfig.field || !sortConfig.dir) return filteredClaims;
        const { field, dir } = sortConfig;
        const mult = dir === 'asc' ? 1 : -1;
        return [...filteredClaims].sort((a, b) => {
            let av, bv;
            if (field === 'slNo') {
                // Sl. No. is just the array index — sort is meaningless unless we use another key
                return 0;
            } else if (field === 'availedCL') {
                av = a.leave_details?.cl ?? 0; bv = b.leave_details?.cl ?? 0;
            } else if (field === 'llp') {
                av = a.leave_details?.llp ?? 0; bv = b.leave_details?.llp ?? 0;
            } else if (field === 'balCL') {
                av = a.balCL ?? 0; bv = b.balCL ?? 0;
            } else if (field === 'amount') {
                av = a.claim_amount ?? 0; bv = b.claim_amount ?? 0;
            } else if (field === 'status') {
                av = a.isadminApproved || 'pending'; bv = b.isadminApproved || 'pending';
            } else if (field === 'tceRollNo') {
                av = a.tceRollNo || ''; bv = b.tceRollNo || '';
            } else if (field === 'name') {
                av = a.scholarName || ''; bv = b.scholarName || '';
            } else if (field === 'dept') {
                av = a.department || ''; bv = b.department || '';
            } else if (field === 'enrolledMonthYear') {
                av = a.enrolledMonthYear || ''; bv = b.enrolledMonthYear || '';
            } else {
                av = a[field] ?? ''; bv = b[field] ?? '';
            }
            if (NUMERIC_SORT.has(field)) return mult * ((Number(av) || 0) - (Number(bv) || 0));
            return mult * av.toString().toLowerCase().localeCompare(bv.toString().toLowerCase());
        });
    })();

    return (
        <Layout title="Manage Claims">
            {/* Stat Cards */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: '1rem',
                marginBottom: '2rem'
            }}>
                <div style={{ background: '#dbeafe', padding: '1.25rem', borderRadius: '0.5rem' }}>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1e40af' }}>{stats.total}</div>
                    <div style={{ color: '#3b82f6' }}>HOD-Approved Claims</div>
                </div>
                <div style={{ background: '#fef3c7', padding: '1.25rem', borderRadius: '0.5rem' }}>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#92400e' }}>{stats.pending}</div>
                    <div style={{ color: '#d97706' }}>Pending Dean(R&D) Review</div>
                </div>
                <div style={{ background: '#dcfce7', padding: '1.25rem', borderRadius: '0.5rem' }}>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#166534' }}>{stats.approved}</div>
                    <div style={{ color: '#22c55e' }}>Associate Dean (R&D) Approved</div>
                </div>
                <div style={{ background: '#fee2e2', padding: '1.25rem', borderRadius: '0.5rem' }}>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#991b1b' }}>{stats.rejected}</div>
                    <div style={{ color: '#ef4444' }}>Rejected</div>
                </div>
                <div style={{ background: '#f3e8ff', padding: '1.25rem', borderRadius: '0.5rem' }}>
                    <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#6b21a8' }}>{formatCurrency(stats.totalAmount)}</div>
                    <div style={{ color: '#9333ea' }}>Total Amount</div>
                </div>
            </div>

            <Card title="Select Claim Period">
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'end' }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                        <span>Filter by</span>
                        <select className="form-input" value={filterMode} onChange={e => setFilterMode(e.target.value)}>
                            <option value="month">Month and Year</option>
                            <option value="range">Custom Date Range</option>
                        </select>
                    </label>
                    {filterMode === 'month' ? (
                        <>
                            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                                <span>Month</span>
                                <select className="form-input" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}>
                                    <option value="">Select month</option>
                                    {MONTHS.map(month => <option key={month} value={month}>{month}</option>)}
                                </select>
                            </label>
                            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                                <span>Year</span>
                                <select className="form-input" value={selectedYear} onChange={e => setSelectedYear(e.target.value)}>
                                    <option value="">Select year</option>
                                    {YEARS.map(year => <option key={year} value={year}>{year}</option>)}
                                </select>
                            </label>
                        </>
                    ) : (
                        <>
                            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                                <span>From</span>
                                <input className="form-input" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
                            </label>
                            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                                <span>To</span>
                                <input className="form-input" type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
                            </label>
                        </>
                    )}
                    <Button variant="primary" onClick={fetchClaims} disabled={loading}>
                        {loading ? 'Loading...' : 'Get Claims'}
                    </Button>
                </div>
            </Card>

            {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}
            {success && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>{success}</div>}

            <Card title="HOD-Approved Claims">
                {/* Filter & Search Bar */}
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.25rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    <input
                        type="text"
                        className="form-input"
                        placeholder="Search by name, roll no, email, dept, month..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{ flex: 1, minWidth: '220px' }}
                    />
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        {['all', 'pending', 'approved', 'rejected'].map(status => (
                            <button
                                key={status}
                                onClick={() => setFilterStatus(status)}
                                style={{
                                    padding: '0.5rem 1rem', border: 'none', borderRadius: '0.375rem', cursor: 'pointer',
                                    fontWeight: '500', fontSize: '0.875rem',
                                    background: filterStatus === status ? '#3b82f6' : '#e5e7eb',
                                    color: filterStatus === status ? 'white' : '#374151'
                                }}
                            >
                                {status.charAt(0).toUpperCase() + status.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Bulk Approve Bar */}
                {selectedIds.size > 0 && (
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: '1rem',
                        padding: '0.75rem 1rem', background: '#eff6ff',
                        borderRadius: '0.5rem', marginBottom: '1rem',
                        border: '1px solid #bfdbfe'
                    }}>
                        <span style={{ fontWeight: '600', color: '#1d4ed8' }}>
                            {selectedIds.size} claim(s) selected
                        </span>
                        <button
                            onClick={handleBulkApprove}
                            disabled={bulkProcessing}
                            style={{
                                padding: '0.5rem 1.25rem', background: '#16a34a', color: 'white',
                                border: 'none', borderRadius: '0.375rem', cursor: 'pointer',
                                fontWeight: '600', fontSize: '0.875rem'
                            }}
                        >
                            {bulkProcessing ? 'Approving...' : `✓ Approve All Selected`}
                        </button>
                        <button
                            onClick={() => setSelectedIds(new Set())}
                            style={{
                                padding: '0.5rem 0.75rem', background: '#e5e7eb',
                                border: 'none', borderRadius: '0.375rem', cursor: 'pointer',
                                fontSize: '0.875rem', color: '#374151'
                            }}
                        >
                            Clear
                        </button>
                    </div>
                )}

                {/* Claims Table */}
                {claims === null ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Select a period above and click Get Claims.</div>
                ) : loading ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Loading claims...</div>
                ) : filteredClaims.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No claims found.</div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                    <th style={thStyle}>
                                        <input
                                            type="checkbox"
                                            checked={allPendingSelected}
                                            onChange={toggleSelectAll}
                                            title="Select all pending"
                                        />
                                    </th>
                                    {[
                                        { label: 'Sl. No.', field: 'slNo' },
                                        { label: 'TCE Roll No', field: 'tceRollNo' },
                                        { label: 'Name', field: 'name' },
                                        { label: 'Dept.', field: 'dept' },
                                        { label: 'Enrolled Month & Year', field: 'enrolledMonthYear' },
                                        { label: 'Availed CL', field: 'availedCL' },
                                        { label: 'Bal. CL', field: 'balCL' },
                                        { label: 'LLP', field: 'llp' },
                                        { label: 'Amount', field: 'amount' },
                                        { label: 'Status', field: 'status' },
                                    ].map(col => (
                                        <th
                                            key={col.field}
                                            style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }}
                                            onClick={() => cycleSort(col.field)}
                                            title={`Sort by ${col.label}`}
                                        >
                                            {col.label}&nbsp;{sortIcon(col.field)}
                                        </th>
                                    ))}
                                    <th style={thStyle}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedClaims.map((claim, index) => {
                                    const isPending = !claim.isadminApproved || claim.isadminApproved === 'pending';
                                    const isSelected = selectedIds.has(claim.id);
                                    return (
                                        <tr
                                            key={claim.id}
                                            style={{
                                                borderBottom: '1px solid #e2e8f0',
                                                background: isSelected ? '#eff6ff' : index % 2 === 0 ? '#ffffff' : '#f9fafb',
                                                transition: 'background 0.15s'
                                            }}
                                        >
                                            <td style={tdStyle}>
                                                {isPending && (
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => toggleSelect(claim.id)}
                                                    />
                                                )}
                                            </td>
                                            <td style={{ ...tdStyle, color: '#6b7280' }}>{index + 1}</td>
                                            <td style={{ ...tdStyle, fontWeight: '600' }}>{claim.tceRollNo || '-'}</td>
                                            <td style={tdStyle}>{claim.scholarName || '-'}</td>
                                            <td style={tdStyle}>{claim.department || '-'}</td>
                                            <td style={tdStyle}>{claim.enrolledMonthYear || '-'}</td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                {claim.leave_details?.cl ?? '-'}
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                {claim.balCL ?? '-'}
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                {claim.leave_details?.llp ?? '-'}
                                            </td>
                                            <td style={{ ...tdStyle, fontWeight: '600', color: '#059669' }}>
                                                {formatCurrency(claim.claim_amount)}
                                            </td>
                                            <td style={tdStyle}>{getStatusBadge(claim)}</td>
                                            <td style={tdStyle}>
                                                <button
                                                    onClick={() => navigate(`/admin/claim/${claim.id}`)}
                                                    style={{
                                                        padding: '0.3rem 0.85rem',
                                                        background: '#3b82f6', color: 'white',
                                                        border: 'none', borderRadius: '0.375rem',
                                                        cursor: 'pointer', fontWeight: '600',
                                                        fontSize: '0.8rem'
                                                    }}
                                                >
                                                    Review
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                <div style={{ marginTop: '2rem' }}>
                    <Button variant="secondary" onClick={() => navigate('/admin/dashboard')}>
                        Back to Dashboard
                    </Button>
                </div>
            </Card>
        </Layout>
    );
};

// Table style helpers
const thStyle = {
    padding: '0.75rem 1rem',
    textAlign: 'left',
    fontWeight: '600',
    color: '#374151',
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    whiteSpace: 'nowrap'
};

const tdStyle = {
    padding: '0.75rem 1rem',
    color: '#1f2937',
    verticalAlign: 'middle'
};

export default ManageClaims;
